import type { CashAccountChoice } from '../api/cash';
import { typedNumber } from '../catalogue/forms';
import type { CashAccountKind, PaymentMethod } from '../generated/enums';

/**
 * One payment split across accounts — **a box per account, the total adds itself up** (owner,
 * 2026-11-01, canvas "Supplier payment and choice", Pay B; "Payment In & Out will be like this" in the
 * whole application, `docs/Split_Payments_Plan.md`). ₹6,000 against the till and ₹4,000 by UPI into
 * HDFC is two parts; each is saved as its own payment from (or into) its own account, so every
 * account's day book is right.
 *
 * <p>
 * The account decides what the method may be — cash from a till or petty cash, anything else from a
 * bank — the same rule `cash/paidFrom.ts` holds for a single payment (`takes`).
 * </p>
 */

/** What can be paid from, or into, an account of this kind. Adjustment moves no money. */
export function methodsFor(kind: CashAccountKind): PaymentMethod[] {
  return kind === 'Bank' ? ['Upi', 'BankTransfer', 'Card', 'Cheque'] : ['Cash'];
}

/** One account's box, as typed. */
export interface SplitPart {
  accountId: number;
  accountName: string;
  kind: CashAccountKind;
  method: PaymentMethod;
  amount: string;
  reference: string;
}

const kindOrder: Record<CashAccountKind, number> = { Till: 0, Bank: 1, PettyCash: 2 };

/**
 * A box per account, empty: the till first, then the banks, then petty cash, each kind's default
 * first, then by name. A bank starts on UPI — the way most shops are paid and pay now.
 */
export function splitParts(accounts: readonly CashAccountChoice[] | undefined): SplitPart[] {
  return [...(accounts ?? [])]
    .sort(
      (a, b) =>
        kindOrder[a.kind] - kindOrder[b.kind] ||
        Number(b.isDefault) - Number(a.isDefault) ||
        a.name.localeCompare(b.name),
    )
    .map((account) => ({
      accountId: account.id,
      accountName: account.name,
      kind: account.kind,
      method: account.kind === 'Bank' ? 'Upi' : 'Cash',
      amount: '',
      reference: '',
    }));
}

/** Changes one account's box, leaving the others alone. */
export function setPart(
  parts: readonly SplitPart[],
  accountId: number,
  changes: Partial<Pick<SplitPart, 'amount' | 'method' | 'reference'>>,
): SplitPart[] {
  return parts.map((part) => (part.accountId === accountId ? { ...part, ...changes } : part));
}

/**
 * A quick amount ("Everything · ₹18,500") puts the whole of it in one box and empties the rest —
 * the box already in use if exactly one is, else the first.
 */
export function fillOne(parts: readonly SplitPart[], amount: number): SplitPart[] {
  const used = parts.filter((part) => typedNumber(part.amount) !== null);
  const target = used.length === 1 ? used[0]!.accountId : parts[0]?.accountId;

  return parts.map((part) => ({
    ...part,
    amount: part.accountId === target ? String(amount) : '',
  }));
}

/** The boxes with money in them — what gets sent, in the order shown. */
export function filledParts(parts: readonly SplitPart[]): SplitPart[] {
  return parts.filter((part) => {
    const amount = typedNumber(part.amount);

    return amount !== null && !Number.isNaN(amount) && amount > 0;
  });
}

/** The sum of every readable box; a half-typed one counts nothing until it reads. */
export function splitTotal(parts: readonly SplitPart[]): number {
  return (
    Math.round(
      filledParts(parts).reduce((sum, part) => sum + (typedNumber(part.amount) ?? 0), 0) * 100,
    ) / 100
  );
}

/** What stops the payment going: nothing typed, or a box that is not a figure above zero. */
export function splitProblem(
  parts: readonly SplitPart[],
  direction: 'from' | 'into' = 'from',
): string | null {
  const bad = parts.find((part) => {
    const amount = typedNumber(part.amount);

    return amount !== null && (Number.isNaN(amount) || amount < 0);
  });

  if (bad) {
    return `The amount for ${bad.accountName} must be a figure above zero.`;
  }

  if (filledParts(parts).length === 0) {
    return direction === 'into'
      ? 'Type how much, in the box of the account it goes into.'
      : 'Type how much, in the box of the account it is paid from.';
  }

  return null;
}

/**
 * "₹6,000 cash from Till, ₹4,000 UPI from HDFC" — the foot's sentence, so the clerk reads what will
 * be saved before pressing Pay. `direction` is "from" for money out, "into" for money in.
 */
export function splitWords(
  parts: readonly SplitPart[],
  methodWord: (method: PaymentMethod) => string,
  rupees: (amount: number) => string,
  direction: 'from' | 'into',
): string {
  return filledParts(parts)
    .map(
      (part) =>
        `${rupees(typedNumber(part.amount) ?? 0)} ${methodWord(part.method)} ${direction} ${part.accountName}`,
    )
    .join(', ');
}

/**
 * The boxes set against what is owed, in order — each box pays the first thing still owed, then the
 * next: ₹4,000 in the till and ₹3,000 by UPI against ₹2,000 then ₹5,000 is ₹2,000 cash to the first,
 * ₹2,000 cash and ₹3,000 UPI to the second. Never more than is owed; what the boxes do not reach is
 * left owing. A row per box per thing owed, so every payment still names one account.
 */
export function allocateParts<K>(
  owed: readonly { kind: K; amount: number }[],
  parts: readonly SplitPart[],
): { kind: K; part: SplitPart; amount: number }[] {
  const left = owed.map((due) => ({ kind: due.kind, left: Math.max(0, due.amount) }));
  const rows: { kind: K; part: SplitPart; amount: number }[] = [];

  for (const part of filledParts(parts)) {
    let amount = typedNumber(part.amount) ?? 0;

    for (const due of left) {
      const take = Math.round(Math.min(amount, due.left) * 100) / 100;

      if (take <= 0) {
        continue;
      }

      rows.push({ kind: due.kind, part, amount: take });
      due.left = Math.round((due.left - take) * 100) / 100;
      amount = Math.round((amount - take) * 100) / 100;
    }
  }

  return rows;
}

/** What a split payment came to: the parts saved, each with what the server answered, and the one refused. */
export interface PaidInParts<T> {
  saved: { part: SplitPart; result: T }[];
  /** The part the server refused, after some were saved; null when every part went through. */
  failed: { part: SplitPart; reason: unknown } | null;
}

/**
 * Sends each filled part in the order shown, one request each, and stops at the first refusal.
 * **Nothing saved and refused → it throws**, like a single payment; once something is saved it
 * answers what was (`failed` says the rest), so the screen can say both and empty the saved boxes —
 * pressing Pay again never pays twice. Money out and money in both send through this.
 */
export async function payInParts<T>(
  parts: readonly SplitPart[],
  send: (part: SplitPart, amount: number) => Promise<T>,
): Promise<PaidInParts<T>> {
  const saved: PaidInParts<T>['saved'] = [];

  for (const part of filledParts(parts)) {
    try {
      saved.push({ part, result: await send(part, typedNumber(part.amount) ?? 0) });
    } catch (reason) {
      if (saved.length === 0) {
        throw reason;
      }

      return { saved, failed: { part, reason } };
    }
  }

  return { saved, failed: null };
}

/** The boxes after a part-way payment: what was saved is emptied, what was not stays to send again. */
export function withoutSaved(
  parts: readonly SplitPart[],
  saved: readonly { part: SplitPart }[],
): SplitPart[] {
  const done = new Set(saved.map((each) => each.part.accountId));

  return parts.map((part) =>
    done.has(part.accountId) ? { ...part, amount: '', reference: '' } : part,
  );
}
